/** @fileoverview Normalizes live and saved tool and skill activity for chat rendering. */

import { toSkillReadData } from '../../lib/skillRead';
import type { FabrikUIMessage, ToolActivity } from '../../types/chat';
import type { ActivityEntry, LiveToolPart, SkillActivityEntry, ToolActivityEntry } from './types';

const getToolName = (part: LiveToolPart) =>
  part.type === 'dynamic-tool' ? part.toolName : part.type.slice(5);

/** Skill reads are surfaced as plain statuses, never as expandable tool calls. */
const isReadSkillTool = (toolName: string) =>
  toolName.replace(/[-\s]/g, '_').toLowerCase() === 'read_skill';
const isProgressTool = (toolName: string) =>
  toolName.replace(/[-\s]/g, '').toLowerCase() === 'reportprogress';

const isLiveToolPart = (part: FabrikUIMessage['parts'][number]): part is LiveToolPart =>
  'toolCallId' in part &&
  'state' in part &&
  (part.type === 'dynamic-tool' || part.type.startsWith('tool-'));

const toToolStatus = (part: LiveToolPart): ToolActivity['status'] =>
  part.state === 'output-available'
    ? 'completed'
    : part.state === 'output-error' || part.state === 'output-denied'
    ? 'failed'
    : 'running';

const getActivityLabel = (part: LiveToolPart) => {
  const metadata = 'toolMetadata' in part ? part.toolMetadata : undefined;
  const activityLabel =
    metadata && typeof metadata.activityLabel === 'string' ? metadata.activityLabel : undefined;
  return activityLabel || undefined;
};

/**
 * Starts a status line with a capital letter, since the model writes its step intents in any case.
 * @param text the status text
 */
export const startWithCapital = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

const getIntent = (part: LiveToolPart) => {
  const metadata = 'toolMetadata' in part ? part.toolMetadata : undefined;
  const input =
    'input' in part && part.input && typeof part.input === 'object' && !Array.isArray(part.input)
      ? (part.input as Record<string, unknown>).intent
      : undefined;
  const intent = [input, metadata?.intent].find(
    (value): value is string => typeof value === 'string' && Boolean(value.trim())
  );
  return intent && startWithCapital(intent.trim());
};

const toToolEntry = (part: LiveToolPart): ToolActivityEntry => {
  const activityLabel = getActivityLabel(part);
  const intent = getIntent(part);
  return {
    kind: 'tool',
    key: part.toolCallId,
    toolName: getToolName(part),
    status: toToolStatus(part),
    ...(intent ? { intent } : {}),
    ...(activityLabel ? { activityLabel } : {}),
    ...('input' in part && part.input !== undefined ? { input: part.input } : {}),
    ...('output' in part && part.output !== undefined ? { output: part.output } : {}),
    ...('errorText' in part && part.errorText !== undefined ? { errorText: part.errorText } : {}),
  };
};

/**
 * Merges streamed and persisted activity into one chronologically ordered list.
 * A `Map` keeps the first-seen position of a repeated key while storing its latest state.
 */
export const getActivityEntries = (
  message: FabrikUIMessage,
  savedTools: ToolActivity[] = []
): ActivityEntry[] => {
  const entries = new Map<string, ActivityEntry>();

  for (const part of message.parts) {
    if (isLiveToolPart(part)) {
      if (isReadSkillTool(getToolName(part)) || isProgressTool(getToolName(part))) continue;
      const entry = toToolEntry(part);
      entries.set(`tool:${entry.key}`, entry);
      continue;
    }
    if (part.type === 'data-skill-read') {
      const data = toSkillReadData(part.data);
      if (!data) continue;
      const entry: SkillActivityEntry = { kind: 'skill', key: data.id, ...data };
      entries.set(`skill:${entry.key}`, entry);
    }
  }

  for (const tool of savedTools) {
    if (isReadSkillTool(tool.toolName) || isProgressTool(tool.toolName)) continue;
    const existing = entries.get(`tool:${tool.toolCallId}`);
    const existingIntent = existing?.kind === 'tool' ? existing.intent : undefined;
    const intent = tool.intent || existingIntent;
    const existingLabel = existing?.kind === 'tool' ? existing.activityLabel : undefined;
    const input =
      tool.input !== undefined
        ? tool.input
        : existing?.kind === 'tool'
        ? existing.input
        : undefined;
    const output =
      tool.output !== undefined
        ? tool.output
        : existing?.kind === 'tool'
        ? existing.output
        : undefined;
    const errorText =
      tool.errorText !== undefined
        ? tool.errorText
        : existing?.kind === 'tool'
        ? existing.errorText
        : undefined;
    const activityLabel = tool.activityLabel || existingLabel;
    entries.set(`tool:${tool.toolCallId}`, {
      kind: 'tool',
      key: tool.toolCallId,
      toolName: tool.toolName,
      status: tool.status,
      ...(intent ? { intent } : {}),
      ...(activityLabel ? { activityLabel } : {}),
      ...(input !== undefined ? { input } : {}),
      ...(output !== undefined ? { output } : {}),
      ...(errorText !== undefined ? { errorText } : {}),
    });
  }

  return [...entries.values()];
};

/** Reports whether a message has activity that should keep the thinking section visible. */
export const hasVisibleActivity = (message: FabrikUIMessage): boolean =>
  getActivityEntries(message).length > 0;

export const formatDuration = (duration?: number) => {
  if (duration === undefined || !Number.isFinite(duration)) return 'a few seconds';
  const seconds = Math.max(1, Math.round(duration));
  const minutes = Math.floor(seconds / 60);
  return minutes
    ? `${minutes}m ${seconds % 60}s`
    : `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
};

/** Fallback for tools without a backend-provided activity label. */
export const getToolStatusPhrase = () => 'Working on it';
